export const getVariant = (text) => {
  switch (text) {
    case "Confirm":
    case "Confirm Details":
    case "Continue":
    case "Submit":
    case "Verify":
      return "contained";
    case "Resend":
    case "Skip":
      return "outlined";
    default:
      return "text";
  }
};

export const getPublicAssetUrl = (
  assetPath,
  publicUrl = process.env.PUBLIC_URL || "",
) => {
  const normalizedPublicUrl = publicUrl.replace(/\/+$/, "");
  const normalizedAssetPath = assetPath.replace(/^\/+/, "");

  return `${normalizedPublicUrl}/${normalizedAssetPath}`;
};

export const isEpicHttpUrl = (href) =>
  typeof href === "string" &&
  /^epichttp:\/\/[^\s]+$/i.test(href.trim());

export const getChatLinkTarget = (href, options = {}) => {
  // Keep the public helper signature for existing callers and future typings.
  void options;
  return isEpicHttpUrl(href) ? "_top" : "_blank";
};

export const getMarkedLinkData = (linkOrHref, title, text) => {
  if (linkOrHref && typeof linkOrHref === "object") {
    return {
      href: linkOrHref.href ?? "",
      text: linkOrHref.text ?? "",
      title: linkOrHref.title ?? null,
    };
  }

  return {
    href: linkOrHref ?? "",
    text: text ?? "",
    title: title ?? null,
  };
};

// Markdown parsers do not treat Epic's custom URL scheme as an autolink.
// Convert bare epichttp URLs into explicit Markdown links while preserving
// the displayed URL and leaving normal http/https handling unchanged.
export const linkifyEpicHttpUrls = (content) => {
  if (typeof content !== "string") {
    return content ?? "";
  }

  return content.replace(
    /(^|[\s(])(epichttp:\/\/[^\s<>"']+)/gi,
    (match, prefix, rawUrl, offset, source) => {
      // Leave an existing Markdown destination such as
      // [Open Messages](epichttp://messages) unchanged.
      if (prefix === "(" && source[offset - 1] === "]") {
        return match;
      }

      let url = rawUrl;
      let trailing = "";

      while (/[.,!?;:)\]]$/.test(url)) {
        trailing = url.slice(-1) + trailing;
        url = url.slice(0, -1);
      }

      return `${prefix}[${url}](${url})${trailing}`;
    },
  );
};

// DOMPurify's default URI allowlist plus Epic's main-frame deep-link scheme.
// Keep this narrow: unknown protocols must remain blocked.
const CHAT_ALLOWED_URI_REGEXP =
  /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|matrix):|epichttp:\/\/[^\s]+$|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i;

export const CHAT_HTML_SANITIZE_CONFIG = {
  ALLOWED_TAGS: ["a", "br"],
  ALLOWED_ATTR: ["href", "title", "class"],
  ALLOWED_URI_REGEXP: CHAT_ALLOWED_URI_REGEXP,
};

export const transformChatLinkUrl = (
  url,
  key,
  node,
  defaultTransform,
) => {
  if (key === "href" && node?.tagName === "a" && isEpicHttpUrl(url)) {
    return url.trim();
  }

  return typeof defaultTransform === "function" ? defaultTransform(url) : "";
};

// Web Chat represents unknown Markdown schemes as citation buttons before it
// sanitizes standard activity text. After Web Chat sanitizes the content, turn
// only its Epic citation buttons back into links that target the main frame.
export const epicLinkMainFrameMiddleware = () => (next) => (request) => {
  const documentFragment = next(request);

  documentFragment
    .querySelectorAll(
      'button.render-markdown__citation[type="button"][value]',
    )
    .forEach((button) => {
      const href = button.getAttribute("value");

      if (!isEpicHttpUrl(href)) {
        return;
      }

      const anchor = button.ownerDocument.createElement("a");

      ["class", "aria-label"].forEach((attribute) => {
        const value = button.getAttribute(attribute);

        if (value !== null) {
          anchor.setAttribute(attribute, value);
        }
      });

      anchor.setAttribute("href", href.trim());
      anchor.setAttribute("target", "_top");
      anchor.append(...button.childNodes);
      button.replaceWith(anchor);
    });

  return documentFragment;
};

export const rewriteSanitizedChatLinks = (
  html,
  {
    documentImpl = typeof document === "undefined" ? null : document,
    getTarget = getChatLinkTarget,
  } = {},
) => {
  if (typeof html !== "string" || !documentImpl) {
    return "";
  }

  const template = documentImpl.createElement("template");
  template.innerHTML = html;

  template.content.querySelectorAll("a").forEach((anchor) => {
    const href = anchor.getAttribute("href");
    anchor.removeAttribute("target");
    anchor.removeAttribute("rel");

    if (!href) {
      return;
    }

    const normalizedHref = isEpicHttpUrl(href) ? href.trim() : href;
    const target = getTarget(normalizedHref) === "_top" ? "_top" : "_blank";
    anchor.setAttribute("href", normalizedHref);
    anchor.setAttribute("target", target);

    if (target === "_blank") {
      anchor.setAttribute("rel", "noopener noreferrer");
    }
  });

  return template.innerHTML;
};

const openDetectableIsolatedWindow = (href, target, openWindow) => {
  const openedWindow = openWindow("", target, undefined);

  if (!openedWindow) {
    return false;
  }

  try {
    openedWindow.opener = null;

    const referrerMeta = openedWindow.document?.createElement?.("meta");
    const appendToHead = openedWindow.document?.head?.appendChild;
    const replaceLocation = openedWindow.location?.replace;

    if (
      !referrerMeta ||
      typeof appendToHead !== "function" ||
      typeof replaceLocation !== "function"
    ) {
      throw new Error("The isolated survey window is not navigable.");
    }

    referrerMeta.name = "referrer";
    referrerMeta.content = "no-referrer";
    appendToHead.call(openedWindow.document.head, referrerMeta);
    replaceLocation.call(openedWindow.location, href);
    return true;
  } catch {
    try {
      openedWindow.close?.();
    } catch {
      // The failed popup is already unusable; continue to the native fallback.
    }

    return false;
  }
};

export const openChatLink = (
  href,
  {
    currentUrl =
      typeof window === "undefined" ? "" : window.location?.href,
    getTarget = getChatLinkTarget,
    openWindow = (...args) => window?.open(...args),
    requireWindowHandle = false,
  } = {},
) => {
  if (typeof href !== "string") {
    return false;
  }

  try {
    const normalizedInput = href.trim();
    const destinationUrl = new URL(normalizedInput, currentUrl || undefined);
    const isEpicLink = isEpicHttpUrl(normalizedInput);

    if (
      !isEpicLink &&
      !["http:", "https:"].includes(destinationUrl.protocol)
    ) {
      return false;
    }

    const normalizedHref = isEpicLink ? normalizedInput : destinationUrl.href;
    const target = getTarget(normalizedHref);
    const shouldReceiveHandle = requireWindowHandle && target === "_blank";

    if (shouldReceiveHandle) {
      return openDetectableIsolatedWindow(normalizedHref, target, openWindow);
    }

    const windowFeatures =
      target === "_blank" ? "noopener,noreferrer" : undefined;
    const openedWindow = openWindow(normalizedHref, target, windowFeatures);

    if (!requireWindowHandle) {
      return true;
    }

    return Boolean(openedWindow);
  } catch {
    return false;
  }
};

export const createChatCardActionMiddleware = ({
  getTarget = getChatLinkTarget,
  openWindow = (...args) => window?.open(...args),
} = {}) =>
  () =>
  (next) =>
  (...args) => {
    const [{ cardAction } = {}] = args;
    const { type, value } = cardAction || {};

    if (type !== "openUrl") {
      return next(...args);
    }

    if (typeof value === "string") {
      openChatLink(value, { getTarget, openWindow });
    }
    return undefined;
  };

export const emitData = (data) => {
  try {
    window.parent?.postMessage(data, "*");
  } catch (error) {
    console.error("Unable to emit bot event to parent window.", error);
  }

  let mobileData;
  try {
    // Mobile needs stringified data.
    mobileData = JSON.stringify(data);
  } catch (error) {
    console.error("Unable to serialize bot event for native bridges.", error);
    return;
  }

  try {
    if (typeof window.android?.botEvents === "function") {
      window.android.botEvents(mobileData);
    }
  } catch (error) {
    console.error("Unable to emit bot event to Android.", error);
  }

  try {
    const botEventsHandler = window.webkit?.messageHandlers?.botEvents;
    if (typeof botEventsHandler?.postMessage === "function") {
      botEventsHandler.postMessage(mobileData);
    }
  } catch (error) {
    console.error("Unable to emit bot event to iOS.", error);
  }
};

export const emitMobileCloseBot = ({
  android = window.android,
  webkit = window.webkit,
} = {}) => {
  let emitted = false;

  try {
    if (typeof android?.closeBot === "function") {
      android.closeBot();
      emitted = true;
    }
  } catch (error) {
    console.error("Unable to invoke Android closeBot bridge.", error);
  }

  try {
    const closeBotHandler = webkit?.messageHandlers?.closeBot;
    if (typeof closeBotHandler?.postMessage === "function") {
      closeBotHandler.postMessage(null);
      emitted = true;
    }
  } catch (error) {
    console.error("Unable to invoke iOS closeBot bridge.", error);
  }

  return emitted;
};

export function getUserBrowserDetails() {
  const userAgent = navigator.userAgent;

  return {
    userAgent: userAgent,
  };
}

export function getPlatform() {
  const platform = new URLSearchParams(window.location.search)
    .get("platform")
    ?.trim()
    .toLowerCase();

  if (["ios", "android", "web"].includes(platform)) {
    return platform;
  }

  const userAgent = navigator.userAgent;

  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return "ios";
  }

  if (/Android/i.test(userAgent)) {
    return "android";
  }

  return "web";
}

export function isSafariBrowser() {
  const userAgent = navigator.userAgent;
  const vendor = navigator.vendor;

  // Detect Safari while excluding Chrome/Edge/Firefox variants on Apple devices.
  return (
    /Safari/i.test(userAgent) &&
    /Apple/i.test(vendor) &&
    !/Chrome|CriOS|FxiOS|Edg/i.test(userAgent)
  );
}

const CLIENT_PLATFORM_INFO_CONVERSATIONS_KEY =
  "clientPlatformInfoSentConversationIds";

function getLocalStorage() {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

function removeStoredConversation(storage) {
  try {
    storage?.removeItem("conversationId");
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
}

export function readStoredConversation(storage = getLocalStorage()) {
  try {
    const storedValue = storage?.getItem("conversationId");

    if (!storedValue) {
      return null;
    }

    const conversation = JSON.parse(storedValue);
    const hasValidId =
      typeof conversation?.id === "string" &&
      conversation.id.trim().length > 0;
    const hasValidTime =
      typeof conversation?.time === "number" &&
      Number.isFinite(conversation.time);

    if (!hasValidId || !hasValidTime) {
      removeStoredConversation(storage);
      return null;
    }

    return conversation;
  } catch {
    removeStoredConversation(storage);
    return null;
  }
}

function readConversationIds(storage, storageKey) {
  try {
    const conversationIds = JSON.parse(storage?.getItem(storageKey) || "[]");
    return Array.isArray(conversationIds)
      ? conversationIds.filter((id) => typeof id === "string" && id)
      : [];
  } catch {
    return [];
  }
}

export function createConversationEventGuard({
  storage = getLocalStorage(),
  storageKey = CLIENT_PLATFORM_INFO_CONVERSATIONS_KEY,
} = {}) {
  let sentConversationIds = new Set(readConversationIds(storage, storageKey));
  let inFlightConversationIds = new Set();

  const shouldSend = (conversationId) => {
    if (
      !conversationId ||
      sentConversationIds.has(conversationId) ||
      inFlightConversationIds.has(conversationId)
    ) {
      return false;
    }

    inFlightConversationIds = new Set([
      ...inFlightConversationIds,
      conversationId,
    ]);
    return true;
  };

  shouldSend.markSent = (conversationId) => {
    inFlightConversationIds = new Set(
      [...inFlightConversationIds].filter((id) => id !== conversationId),
    );

    const nextSentConversationIds = new Set([...sentConversationIds, conversationId]);
    const maxStoredConversationIds = 50;
    const trimmedConversationIds = [...nextSentConversationIds].slice(-maxStoredConversationIds);

    sentConversationIds = new Set(trimmedConversationIds);
    try {
      storage?.setItem(storageKey, JSON.stringify(trimmedConversationIds));
    } catch {
      // The in-memory guard still prevents duplicates when storage is unavailable.
    }
  };

  shouldSend.markFailed = (conversationId) => {
    inFlightConversationIds = new Set(
      [...inFlightConversationIds].filter((id) => id !== conversationId),
    );
  };

  return shouldSend;
}

export async function fetchDirectLineConnection({
  authorization,
  fetchImpl = fetch,
  fallbackConversationId = "",
  signal,
  url,
}) {
  const response = await fetchImpl(url, {
    method: "GET",
    headers: {
      Authorization: authorization,
    },
    signal,
  });
  const data = await response.json();

  if (signal?.aborted) {
    return null;
  }

  return {
    conversationId: data.conversationId || fallbackConversationId,
    streamUrl: data.streamUrl,
    token: data.token,
  };
}

export async function reconnectWithIosStorageRecovery({
  connect,
  conversationId,
  isMobile,
  platform,
  signal,
  storage = getLocalStorage(),
}) {
  const canRecoverStoredIosConversation =
    !isMobile && platform === "ios" && Boolean(conversationId);

  try {
    const connection = await connect(conversationId);

    if (
      signal?.aborted ||
      (typeof connection?.token === "string" && connection.token.trim()) ||
      !canRecoverStoredIosConversation
    ) {
      return signal?.aborted ? null : connection;
    }
  } catch (error) {
    if (
      signal?.aborted ||
      error?.name === "AbortError" ||
      !canRecoverStoredIosConversation
    ) {
      throw error;
    }
  }

  removeStoredConversation(storage);

  if (signal?.aborted) {
    return null;
  }

  return connect("");
}

const USER_TRANSCRIPT_STORAGE_PREFIX = "hope:user-transcript:";
const MAX_PERSISTED_USER_ACTIVITIES = 50;

export function getUserTranscriptStorageKey(conversationId) {
  return `${USER_TRANSCRIPT_STORAGE_PREFIX}${conversationId || ""}`;
}

function getActivityFingerprint(activity) {
  return [
    activity?.id || "",
    activity?.channelData?.clientActivityID || "",
    activity?.timestamp || "",
    activity?.text || "",
  ].join("|");
}

function isPersistableUserActivity(activity) {
  return Boolean(
    activity?.type === "message" &&
      activity?.from?.role === "user" &&
      (typeof activity?.text === "string" ||
        Array.isArray(activity?.attachments)),
  );
}

export function readUserTranscriptActivities(storage, conversationId) {
  if (!storage || !conversationId) {
    return [];
  }

  try {
    const activities = JSON.parse(
      storage.getItem(getUserTranscriptStorageKey(conversationId)) || "[]",
    );
    return Array.isArray(activities)
      ? activities.filter(isPersistableUserActivity)
      : [];
  } catch {
    return [];
  }
}

export function persistUserTranscriptActivity(
  storage,
  conversationId,
  activity,
  clientActivityID,
) {
  if (!storage || !conversationId || !isPersistableUserActivity(activity)) {
    return false;
  }

  try {
    const activities = readUserTranscriptActivities(storage, conversationId);
    const normalizedActivity = clientActivityID
      ? {
          ...activity,
          channelData: {
            ...activity.channelData,
            clientActivityID,
          },
        }
      : activity;
    const fingerprint = getActivityFingerprint(normalizedActivity);
    const stableIdentity =
      normalizedActivity.id ||
      normalizedActivity.channelData?.clientActivityID ||
      "";
    const existingIndex = activities.findIndex((item) => {
      if (
        normalizedActivity.channelData?.clientActivityID &&
        item.channelData?.clientActivityID ===
          normalizedActivity.channelData.clientActivityID
      ) {
        return true;
      }

      if (getActivityFingerprint(item) === fingerprint) {
        return true;
      }

      // Older locally stored activities may not have a stable identity. If
      // Direct Line now supplies one for the same user text, replace the old
      // copy instead of rendering both copies after reconnect.
      return Boolean(
        stableIdentity &&
          !item.id &&
          !item.channelData?.clientActivityID &&
          item.text === normalizedActivity.text,
      );
    });

    if (existingIndex >= 0) {
      if (getActivityFingerprint(activities[existingIndex]) === fingerprint) {
        return false;
      }

      const updatedActivities = [...activities];
      updatedActivities[existingIndex] = normalizedActivity;
      storage.setItem(
        getUserTranscriptStorageKey(conversationId),
        JSON.stringify(updatedActivities),
      );
      return true;
    }

    storage.setItem(
      getUserTranscriptStorageKey(conversationId),
      JSON.stringify(
        [...activities, normalizedActivity].slice(
          -MAX_PERSISTED_USER_ACTIVITIES,
        ),
      ),
    );
    return true;
  } catch {
    return false;
  }
}

export function clearUserTranscriptActivities(storage, conversationId) {
  if (!storage || !conversationId) {
    return;
  }

  try {
    storage.removeItem(getUserTranscriptStorageKey(conversationId));
  } catch {
    // Storage is best effort. Closing the chat must still complete.
  }
}

export function getActivityConversationId(activity) {
  const conversationId = activity?.conversation?.id;
  return typeof conversationId === "string" ? conversationId : "";
}

export function isResolvedAgeVerificationActivity(activity, isResolved) {
  return Boolean(
    isResolved &&
      activity?.attachments?.some(
        (attachment) =>
          attachment?.content?.subtype === "age_verification",
      ),
  );
}

export function isAcceptedDirectLineActivityId(activityId) {
  return (
    typeof activityId === "string" && Boolean(activityId) && activityId !== "retry"
  );
}

export function resolveDirectLineConversationId(
  connectionConversationId,
  directLine,
) {
  return directLine?.conversationId || connectionConversationId || "";
}

export function sendClientPlatformInfoOnConnection({ directLine, send }) {
  if (!directLine) {
    return false;
  }

  try {
    send(directLine, "prelogin");
    return true;
  } catch {
    return false;
  }
}

export function sendClientPlatformInfoAfterHandoff({
  conversationId,
  directLine,
  handoffAccepted,
  send,
  shouldSend,
}) {
  if (
    !handoffAccepted ||
    !conversationId ||
    !directLine ||
    !shouldSend(conversationId)
  ) {
    return false;
  }

  try {
    send(directLine, "postlogin", {
      onError: () => shouldSend.markFailed(conversationId),
      onSuccess: (activityId) => {
        if (isAcceptedDirectLineActivityId(activityId)) {
          shouldSend.markSent(conversationId);
        } else {
          shouldSend.markFailed(conversationId);
        }
      },
    });
  } catch {
    shouldSend.markFailed(conversationId);
    return false;
  }
  return true;
}
